// components/form/fields/InputField.tsx
import { useFormContext } from "react-hook-form";
import MyBox from "../../MyBox";
import { useState } from "react";
import { Eye, EyeOff, AlertCircle, CheckCircle, XCircle } from "lucide-react";

// formFieldType.ts
export interface FormField {
  name: string;
  label?: string;
  topInfo?: string;
  type?: "text" | "email" | "password" | "number" | "tel" | "url" | "search";
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  button?: React.ReactNode;
  
  // Validation
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  step?: number;
  pattern?: RegExp;
  validate?: (value: any) => boolean | string;
  
  // UI enhancements
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  clearable?: boolean;
  helperText?: string;
  successMessage?: string;
  width?: string | number;
  autoComplete?: string;
  
  // Dependencies
  dependsOn?: string;
  visibleWhen?: { field: string; equals?: any; notEmpty?: boolean };
  
  // Other
  defaultValue?: any;
  onChange?: (value: any) => void;
}

export function InputField({ field }: { field: FormField }) {
  const { register, formState, watch, setValue } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const error = formState.errors[field.name];
  const currentValue = watch(field.name);
  const hasValue = currentValue && String(currentValue).length > 0;
  
  // Determine input type with password toggle support
  const inputType = field.type === "password" 
    ? (showPassword ? "text" : "password")
    : field.type || "text";
  
  // Validation state
  const validationState = field.validate 
    ? !error && hasValue ? "success" : error ? "error" : "default"
    : "default";
  
  const getValidationStyles = () => {
    if (validationState === "success") {
      return "border-green-500 dark:border-green-400 focus:ring-green-500";
    }
    if (validationState === "error") {
      return "border-red-500 dark:border-red-400 focus:ring-red-500";
    }
    return "border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400";
  };
  
  const getIcon = () => {
    if (validationState === "success") {
      return <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />;
    }
    if (validationState === "error") {
      return <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />;
    }
    return null;
  };
  
  // Handle clear button
  const handleClear = () => {
    setValue(field.name, "", { shouldDirty: true, shouldValidate: true });
  };
  
  // Handle max length display
  const currentLength = currentValue ? String(currentValue).length : 0;
  const showCharCount = field.maxLength && currentLength > 0;
  
  return (
    <MyBox topInfo={field?.topInfo} topText={field.label} required={field.required} className="w-full">
      <div className="w-full space-y-1">
        {/* Input wrapper */}
        <div className="relative">
          {/* Prefix support */}
          {field.prefix && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              {field.prefix}
            </div>
          )}
          
          <input
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
              minLength: field.minLength && {
                value: field.minLength,
                message: `${field.label} must be at least ${field.minLength} characters`
              },
              maxLength: field.maxLength && {
                value: field.maxLength,
                message: `${field.label} must be at most ${field.maxLength} characters`
              },
              pattern: field.pattern && {
                value: field.pattern,
                message: `Invalid ${field.label?.toLowerCase()} format`
              },
              validate: field.validate,
              min: field.min && {
                value: field.min,
                message: `${field.label} must be at least ${field.min}`
              },
              max: field.max && {
                value: field.max,
                message: `${field.label} must be at most ${field.max}`
              },
            })}
            type={inputType}
            name={field.name}
            required={field.required}
            placeholder={field.placeholder}
            disabled={field.disabled}
            readOnly={field.readOnly}
            min={field.min}
            max={field.max}
            step={field.step}
            autoComplete={field.autoComplete}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`
              w-full rounded-lg px-3 py-2
              bg-white dark:bg-gray-800
              text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-500
              border transition-all duration-200
              outline-none
              ${getValidationStyles()}
              ${field.disabled ? "bg-gray-100 dark:bg-gray-900 cursor-not-allowed opacity-60" : ""}
              ${field.readOnly ? "bg-gray-50 dark:bg-gray-800/50 cursor-default" : ""}
              ${field.prefix ? "pl-8" : ""}
              ${field.suffix || field.type === "password" || validationState !== "default" ? "pr-10" : "pr-3"}
              ${isFocused ? "ring-2 ring-opacity-20" : ""}
            `}
            style={{
              ...(field.width ? { width: field.width } : {}),
            }}
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {/* Clear button */}
            {field.clearable && hasValue && !field.disabled && !field.readOnly && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Clear input"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
            
            {/* Password toggle */}
            {field.type === "password" && !field.disabled && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
            
            {/* Validation icon */}
            {getIcon()}
            
            {/* Custom suffix */}
            {field.suffix && field.type !== "password" && validationState === "default" && (
              <span className="text-gray-500 dark:text-gray-400 text-sm">{field.suffix}</span>
            )}
          </div>
        </div>
        
        {/* Helper text */}
        {field.helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {field.helperText}
          </p>
        )}
        
        {/* Error message */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {String(error.message)}
          </p>
        )}
        
        {/* Character count */}
        {showCharCount && field.maxLength && (
          <p className={`text-xs mt-1 text-right ${
            currentLength > field.maxLength 
              ? "text-red-500 dark:text-red-400" 
              : "text-gray-500 dark:text-gray-400"
          }`}>
            {currentLength}/{field.maxLength}
          </p>
        )}
        
        {/* Success message */}
        {validationState === "success" && field.successMessage && !error && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {field.successMessage}
          </p>
        )}
      </div>
    </MyBox>
  );
}